import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { useEffect, useReducer, useRef, useState } from 'react';
import useRect from "../helpers/useRect";
import wrapEvent from "../helpers/wrapEvent";
// No action triggered yet
var IDLE = 'idle';

// we're considering showing the tooltip, but we're gonna wait a sec
var FOCUSED = 'focused';

// IT'S ON
var VISIBLE = 'visible';

// Focus has left, but we want to keep it visible for a sec
var LEAVING_VISIBLE = 'leavingVisible';

// The user clicked the tool, so we want to hide the thing, we can't just use
// IDLE because we need to ignore mousemove, etc.
var DISMISSED = 'dismissed';
var stateMachine = function stateMachine() {
  return {
    initial: IDLE,
    states: _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, IDLE, {
      on: {
        mouseenter: FOCUSED,
        focus: VISIBLE
      }
    }), FOCUSED, {
      on: {
        mousemove: FOCUSED,
        mouseleave: IDLE,
        mousedown: DISMISSED,
        keydown: DISMISSED,
        blur: IDLE,
        rest: VISIBLE
      }
    }), VISIBLE, {
      on: {
        focus: FOCUSED,
        mouseenter: FOCUSED,
        mouseleave: LEAVING_VISIBLE,
        blur: LEAVING_VISIBLE,
        mousedown: DISMISSED,
        timecomplete: IDLE
      }
    }), LEAVING_VISIBLE, {
      on: {
        mouseenter: VISIBLE,
        focus: VISIBLE,
        timecomplete: IDLE
      }
    }), DISMISSED, {
      on: {
        mouseleave: IDLE,
        blur: IDLE
      }
    })
  };
};
var _stateMachine = /*#__PURE__*/stateMachine(),
  states = _stateMachine.states;
var transition = function transition(state, event) {
  var _states$state$on$even, _states$state;
  return (_states$state$on$even = (_states$state = states[state]) === null || _states$state === void 0 ? void 0 : _states$state.on[event]) !== null && _states$state$on$even !== void 0 ? _states$state$on$even : state;
};
export default function useTooltip(_ref) {
  var onMouseEnter = _ref.onMouseEnter,
    onMouseMove = _ref.onMouseMove,
    onMouseLeave = _ref.onMouseLeave,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    onKeyDown = _ref.onKeyDown,
    onMouseDown = _ref.onMouseDown,
    forwardedRef = _ref.ref,
    DEBUG = _ref.DEBUG;
  var _useReducer = useReducer(transition, IDLE),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    state = _useReducer2[0],
    dispatch = _useReducer2[1];
  var _useState = useState(typeof DEBUG !== 'undefined' ? DEBUG : false),
    _useState2 = _slicedToArray(_useState, 2),
    isVisible = _useState2[0],
    setIsVisible = _useState2[1];
  var ownRef = useRef();
  var ref = forwardedRef || ownRef;
  var triggerRect = useRect(ref, isVisible);

  // If the user focuses on the element wait 100 ms to show the tooltip
  useEffect(function () {
    if (state === FOCUSED) {
      var timer = setTimeout(function () {
        return dispatch('rest');
      }, 100);
      return function () {
        return clearTimeout(timer);
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function () {};
  }, [state]);

  // If we loose focus from the element, wait 100 ms to hide the tooltip
  useEffect(function () {
    if (state === LEAVING_VISIBLE) {
      setIsVisible(true);
      var timer = setTimeout(function () {
        return dispatch('timecomplete');
      }, 100);
      return function () {
        return clearTimeout(timer);
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function () {};
  }, [state]);

  // Toggle tooltip visibility
  useEffect(function () {
    if (state === VISIBLE || state === LEAVING_VISIBLE) {
      setIsVisible(true);
    } else if (!DEBUG) {
      setIsVisible(false);
    }
  }, [state, DEBUG]);
  var handleMouseEnter = function handleMouseEnter() {
    switch (state) {
      case IDLE:
      case VISIBLE:
      case LEAVING_VISIBLE:
        {
          dispatch('mouseenter');
          break;
        }
      default:
        break;
    }
  };
  var handleMouseMove = function handleMouseMove() {
    switch (state) {
      case FOCUSED:
        {
          dispatch('mousemove');
          break;
        }
      default:
        break;
    }
  };
  var handleFocus = function handleFocus() {
    switch (state) {
      case IDLE:
      case VISIBLE:
      case LEAVING_VISIBLE:
        {
          dispatch('focus');
          break;
        }
      default:
        break;
    }
  };
  var handleBlur = function handleBlur() {
    switch (state) {
      case FOCUSED:
      case VISIBLE:
      case DISMISSED:
        {
          dispatch('blur');
          break;
        }
      default:
        break;
    }
  };
  var handleMouseLeave = function handleMouseLeave() {
    switch (state) {
      case FOCUSED:
      case VISIBLE:
      case DISMISSED:
        {
          dispatch('mouseleave');
          break;
        }
      default:
        break;
    }
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
      dispatch('keydown');
    }
  };
  var handleMouseDown = function handleMouseDown() {
    switch (state) {
      case FOCUSED:
      case VISIBLE:
        {
          dispatch('mousedown');
          break;
        }
      default:
        break;
    }
  };
  var trigger = {
    ref: ref,
    onMouseEnter: wrapEvent(onMouseEnter, handleMouseEnter),
    onMouseMove: wrapEvent(onMouseMove, handleMouseMove),
    onFocus: wrapEvent(onFocus, handleFocus),
    onBlur: wrapEvent(onBlur, handleBlur),
    onMouseLeave: wrapEvent(onMouseLeave, handleMouseLeave),
    onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
    onMouseDown: wrapEvent(onMouseDown, handleMouseDown)
  };
  var tooltip = {
    isVisible: isVisible,
    triggerRect: triggerRect
  };
  return [trigger, tooltip];
}